<script setup lang="ts">
import { computed, ref, shallowRef, watch } from 'vue'
import { DateTime } from 'luxon'
import { useTFACurtailmentIntervals } from '@/composables/cpm/cpmProgramManagement'
import type { ITFACurtailmentIntervalFilters, ITFACurtailmentIntervalRatioFilters } from '@/types/cpm/filters'
import type { TFACurtailmentInterval } from '@/types/cpm'

/**
 * Month-scoped table of a site's transmission-fee-avoidance curtailment
 * intervals. A headline ratio shows how many of the prescribed curtailment
 * hours were adhered to (`curtailed === true`) out of the month's total.
 *
 * @prop siteId   - Selected site id; queries stay disabled until one is provided.
 * @prop timezone - Site timezone used to render each interval's date/hour.
 * @model month   - Selected month (1-12); two-way so a parent (e.g. the bar chart) can drive it.
 * @model year    - Selected year; two-way, see `month`.
 */
const props = defineProps<{ siteId?: string; timezone: string }>()

const monthSelected = defineModel<number>('month', { required: true }) // 1-12
const yearSelected = defineModel<number>('year', { required: true })

const now = DateTime.now()

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
// Current year ±2, plus the selected year if a drill-down (e.g. the "ALL"
// chart range) lands outside that window — so the dropdown always has a match.
const years = computed(() => {
  const base = Array.from({ length: 5 }, (_, i) => now.year - 2 + i)
  return base.includes(yearSelected.value) ? base : [...base, yearSelected.value].sort((a, b) => a - b)
})

const { getTFACurtailmentIntervalsQuery, getCurtailmentRatioQuery } = useTFACurtailmentIntervals()

/** Default page — a month of curtailments virtually always fits. */
const DEFAULT_PER_PAGE = 100
/** "Load all" page: every hour of a 30-day month, past any realistic schedule. */
const EXPANDED_PER_PAGE = 24 * 30

// Opt-in wider page, driven by the footer's "Load all". Reset whenever the scope
// changes so one busy month doesn't leave every later month fetching 720 rows.
const loadAll = ref(false)
watch([() => props.siteId, monthSelected, yearSelected], () => {
  loadAll.value = false
})

// Intervals feeding the table. Single page by design — the footer widens it on demand
// rather than paginating, since the list is already scoped to one month.
const intervalFilters = computed<ITFACurtailmentIntervalFilters>(() => ({
  site_id: props.siteId,
  year: yearSelected.value,
  month: monthSelected.value,
  page: 1,
  per_page: loadAll.value ? EXPANDED_PER_PAGE : DEFAULT_PER_PAGE,
}))

const ratioFilters = computed<ITFACurtailmentIntervalRatioFilters>(() => ({
  site_id: props.siteId,
  year: yearSelected.value,
  month: monthSelected.value,
}))

const { data: intervalsData, isLoading } = getTFACurtailmentIntervalsQuery(intervalFilters)
const { data: ratioData } = getCurtailmentRatioQuery(ratioFilters)

/** Chronological intervals for the selected month. */
const intervals = computed<TFACurtailmentInterval[]>(() =>
  [...(intervalsData.value?.data ?? [])].sort((a, b) => a.start_datetime_utc.localeCompare(b.start_datetime_utc))
)

/** What the server holds for this month, regardless of how much we fetched. */
const totalRecords = computed(() => intervalsData.value?.total_records ?? 0)

/** The month has rows we haven't fetched — the footer offers to widen the page. */
const hasUnloadedRows = computed(() => totalRecords.value > intervals.value.length)

/**
 * Even the expanded page can't cover this month, so there's nothing left to offer
 * and raising the cap is a backend conversation.
 */
const atLoadCeiling = computed(() => loadAll.value && totalRecords.value > EXPANDED_PER_PAGE)

function inSiteZone(iso: string): DateTime | null {
  if (!iso) return null
  return DateTime.fromISO(iso, { zone: 'utc' }).setZone(props.timezone || 'utc')
}

/**
 * Full, unambiguous datetime in the site's zone (e.g. "Jul 20, 2026, 18:00 CDT").
 * Matches the Program Management intervals table so the two read identically.
 */
function formatDatetimeInSiteTz(_row: TFACurtailmentInterval, _col: unknown, value: string): string {
  const dt = inSiteZone(value)
  return dt && dt.isValid ? dt.toFormat('MMM d, yyyy, HH:mm ZZZZ') : '—'
}

/**
 * The fee-avoiding curtailment: the single row whose window caught the month's
 * coincident-peak hour. Both flags must hold — a curtailed row that covers the
 * peak is what earns the avoidance.
 */
function isCpRow(row: TFACurtailmentInterval): boolean {
  return row.covers_cp_hour && row.curtailed
}

/** Exact CP-hour instant in the site's zone; a curtailment can span hours, so this pinpoints it. */
function formatCpHour(row: TFACurtailmentInterval): string {
  const dt = row.cp_hour_utc ? inSiteZone(row.cp_hour_utc) : null
  return dt && dt.isValid ? dt.toFormat('MM/dd/yyyy HH:mm') : '—'
}

function rowClassName({ row }: { row: TFACurtailmentInterval }): string {
  return isCpRow(row) ? 'cp-hour-row' : ''
}

// Row-level CP-hour tooltip. el-table has no per-row tooltip, so we drive one
// manually: anchor a virtual-triggered tooltip to the hovered row's <tr> and
// show it while any cell of the fee-avoiding row is hovered.
const cpTooltipVisible = ref(false)
const cpTooltipContent = ref('')
const cpTooltipRef = shallowRef<HTMLElement>()
// Brief grace period so crossing between cells of the same row doesn't flicker.
let hideTimer: ReturnType<typeof setTimeout> | undefined

function onCellEnter(row: TFACurtailmentInterval, _col: unknown, cell: HTMLElement): void {
  if (!isCpRow(row)) return
  clearTimeout(hideTimer)
  cpTooltipContent.value = `Coincident-peak hour: ${formatCpHour(row)}`
  cpTooltipRef.value = (cell.closest('tr') as HTMLElement) ?? cell
  cpTooltipVisible.value = true
}

function onCellLeave(): void {
  hideTimer = setTimeout(() => {
    cpTooltipVisible.value = false
  }, 80)
}
</script>

<template>
  <div class="h-full flex flex-col gap-3 min-h-0">
    <div class="flex items-start justify-between gap-3 shrink-0">
      <span class="uppercase text-xs text-text-tertiary">Historical and Future Curtailments</span>
      <div class="flex gap-1.5">
        <span class="text-2xl font-semibold leading-none">{{ ratioData?.curtailed_hours ?? '-' }}</span>
        <span class="text-2xl text-text-tertiary leading-none">/ {{ ratioData?.total_hours }}</span>
      </div>
    </div>

    <div class="flex gap-2 shrink-0">
      <el-select v-model="monthSelected" class="w-40">
        <el-option v-for="(name, index) in MONTHS" :key="index" :value="index + 1" :label="name" />
      </el-select>
      <el-select v-model="yearSelected" class="w-28">
        <el-option v-for="yr in years" :key="yr" :value="yr" :label="String(yr)" />
      </el-select>
    </div>

    <div class="flex-1 min-h-0">
      <el-table
        v-loading="isLoading"
        :data="intervals"
        :row-class-name="rowClassName"
        height="100%"
        style="width: 100%"
        @cell-mouse-enter="onCellEnter"
        @cell-mouse-leave="onCellLeave"
      >
        <template #empty>
          <el-empty description="No curtailments for this month." />
        </template>
        <el-table-column label="Start" prop="start_datetime_utc" :formatter="formatDatetimeInSiteTz" />
        <el-table-column label="End" prop="end_datetime_utc" :formatter="formatDatetimeInSiteTz" />
        <el-table-column label="Curtailed" width="110" align="center">
          <template #default="{ row }">
            <!-- One-way :model-value + pointer-events-none: keeps the enabled (blue) look but is immutable. -->
            <el-checkbox
              :model-value="(row as TFACurtailmentInterval).curtailed"
              class="pointer-events-none"
              tabindex="-1"
            />
          </template>
        </el-table-column>
      </el-table>

      <!-- Single tooltip anchored to the hovered fee-avoiding row via a virtual trigger. -->
      <el-tooltip
        :visible="cpTooltipVisible"
        :virtual-ref="cpTooltipRef"
        virtual-triggering
        placement="top"
        :content="cpTooltipContent"
      />
    </div>

    <!-- Widen the page on demand; own up to the ceiling when even that isn't enough. -->
    <div v-if="atLoadCeiling || hasUnloadedRows" class="shrink-0 flex justify-center">
      <span v-if="atLoadCeiling" class="text-xs text-text-tertiary">
        You've reached the end. Contact support if you need a higher limit.
      </span>
      <el-button v-else link type="primary" size="small" @click="loadAll = true">
        Load all {{ totalRecords }}
      </el-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Element Plus paints header/row/cell backgrounds through these CSS vars.
// Clearing them lets the card background show through — note `background-color: none`
// is invalid CSS (only the `background` shorthand accepts `none`), which is why
// overriding the cell directly did nothing even with !important.
:deep() {
  .el-table {
    --el-table-bg-color: transparent;
    --el-table-tr-bg-color: transparent;
    --el-table-header-bg-color: transparent;
    --el-table-header-text-color: var(--text-tertiary);
  }

  .el-checkbox__inner {
    border-radius: 4px;
  }

  // Fee-avoiding row: the curtailment that caught the month's coincident-peak hour.
  // Paint the cell background directly (the tr-bg-color var above is cleared to transparent),
  // and hold the highlight through Element Plus's row-hover state. The orange-header fill
  // is the design system's row-highlight token, so it re-resolves per theme.
  .cp-hour-row {
    > td.el-table__cell,
    &:hover > td.el-table__cell {
      background-color: var(--component-fill-orange-header);

      &:first-of-type {
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }

      &:last-of-type {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }
    }
  }
}
</style>
